import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; 'error-callback'?: () => void; theme?: 'light' | 'dark'; appearance?: 'always' | 'execute' | 'interaction-only' }) => string;
      reset: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile-js';

function ensureScript(): Promise<void> {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve();
    if (document.getElementById(SCRIPT_ID)) {
      const interval = setInterval(() => {
        if (window.turnstile) { clearInterval(interval); resolve(); }
      }, 50);
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

export function TurnstileWidget({ siteKey, onToken }: { siteKey: string | null; onToken: (t: string) => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!siteKey) { onToken('dev-bypass'); return; }
    let mounted = true;
    ensureScript().then(() => {
      if (!mounted || !ref.current || !window.turnstile) return;
      const id = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (t) => onToken(t),
        'error-callback': () => onToken(''),
        theme: 'light',
        appearance: 'always',
      });
      widgetIdRef.current = id;
      setReady(true);
    });
    return () => { mounted = false; };
  }, [siteKey, onToken]);

  if (!siteKey) {
    return <div className="text-[11px] text-tatweer-muted italic">Turnstile disabled (dev).</div>;
  }

  return (
    <div className="flex justify-center">
      <div ref={ref} />
      {!ready && <div className="text-[11px] text-tatweer-muted">Loading verification…</div>}
    </div>
  );
}
